import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAgencyContext, useAuth, useTheme, useToast } from '../contexts/AppContexts';
import { processImageFile } from '../utils/imageCompression';
import useImageDrop from '../utils/useImageDrop';
import { ArrowDown, ArrowRight, ArrowUp, Building2, Camera, Edit, Shield, Trash2, Upload, UserCog, UserPlus, Users, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { API_BASE_URL } from '../config';

const API = `${API_BASE_URL}/api`;

const UserManagement = ({ onClose }) => {
  const { theme } = useTheme();
  const { user: currentUser, token } = useAuth();
  const { selectedAgency } = useAgencyContext();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [agencyFilter, setAgencyFilter] = useState('all');
  const [agencies, setAgencies] = useState([]);
  const [agenciesMap, setAgenciesMap] = useState({});
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'User',
    is_active: true,
    agency_registration_code: ''
  });
  const [toggling2FA, setToggling2FA] = useState(false);
  const [agencyInfo, setAgencyInfo] = useState(null);
  const [loadingAgencyInfo, setLoadingAgencyInfo] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [syncingParticipants, setSyncingParticipants] = useState(false);

  // Full bi-directional sync — runs BOTH:
  //   1) Users → Participants (creates missing participants + reconciles matched pairs)
  //   2) Participants → Users (reconciles matched pairs — never creates users, so
  //      unlicensed helpers who exist as participants only stay that way)
  const handleSyncUsersToParticipants = async () => {
    if (!window.confirm(
      'Full Sync (Users ↔ Participants)?\n\n' +
      "• Every user in this agency without a matching Participant will get one created automatically (name / email / phone / avatar carried over).\n" +
      "• Every matched User/Participant pair (by email) will be reconciled so both sides share the same name, email, phone and profile image.\n" +
      "• Participants without a matching user are LEFT ALONE — no user is ever created from a participant, so unlicensed helpers remain participants only.\n\n" +
      'Continue?'
    )) return;

    setSyncingParticipants(true);
    try {
      const [u2p, p2u] = await Promise.all([
        axios.post(`${API}/users/sync-to-participants`, {}, { headers: { Authorization: `Bearer ${token}` } }),
        axios.post(`${API}/participants/sync-to-users`, {}, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const created = u2p.data?.created ?? 0;
      const reconciledU2P = u2p.data?.reconciled ?? 0;
      const reconciledP2U = p2u.data?.reconciled ?? 0;
      const skippedNoUser = p2u.data?.skipped_no_matching_user ?? 0;
      const totalReconciled = Math.max(reconciledU2P, reconciledP2U); // both should be equal after the pair
      toast.success(
        `Full Sync complete — ${created} new participant${created === 1 ? '' : 's'} created, ` +
        `${totalReconciled} matched pair${totalReconciled === 1 ? '' : 's'} reconciled, ` +
        `${skippedNoUser} participant${skippedNoUser === 1 ? '' : 's'} left as participant-only (no matching user).`
      );
    } catch (err) {
      console.error('Sync failed:', err);
      const msg = err.response?.data?.detail || err.message || 'Sync failed';
      toast.error(`Could not sync: ${msg}`);
    } finally {
      setSyncingParticipants(false);
    }
  };

  // Shared user-image processor (used by both file input + drag & drop)
  const processUserImageFile = async (file) => {
    if (!file) return;
    const result = await processImageFile(file, 'profile');
    if (result.success) {
      setEditingUser(prev => (prev ? { ...prev, profile_image: result.data } : prev));
    } else {
      toast.error(result.error || 'Failed to process image');
    }
  };

  const { dropProps: editUserImageDropProps, isDragging: isEditUserImageDragging } = useImageDrop({
    onFile: (file) => processUserImageFile(file)
  });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Fetch agency info for user count and limit
  useEffect(() => {
    const fetchAgencyInfo = async () => {
      if (!currentUser?.organization_id) return;
      
      setLoadingAgencyInfo(true);
      try {
        const response = await axios.get(`${API}/agencies/${currentUser.organization_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAgencyInfo(response.data);
      } catch (error) {
        console.error('Error fetching agency info:', error);
      } finally {
        setLoadingAgencyInfo(false);
      }
    };
    
    if (currentUser?.role === 'Agency Admin' || currentUser?.role === 'Super Admin') {
      fetchAgencyInfo();
    }
  }, [currentUser?.organization_id, token]);

  // Access control - Only Super Admin and Agency Admin can access User Management
  if (currentUser?.role === 'User' || currentUser?.role === 'Viewer') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
        <div className="bg-gray-800 border border-red-500/30 p-8 rounded-lg max-w-md">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="h-8 w-8 text-red-400" />
            <h2 className="text-2xl font-bold text-red-400">Access Denied</h2>
          </div>
          <p className="text-gray-300 mb-6">
            You do not have permission to access User Management. Only Agency Admins and Super Admins can manage users.
          </p>
          <Button onClick={onClose} className="w-full bg-red-500 hover:bg-red-600">
            Close
          </Button>
        </div>
      </div>
    );
  }

  // Fetch agencies list (for Super Admin to populate agency filter)
  useEffect(() => {
    const fetchAgencies = async () => {
      if (currentUser?.role !== 'Super Admin') return;
      
      try {
        const response = await axios.get(`${API}/agencies`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAgencies(response.data);
        
        // Create a map for quick agency name lookup
        const map = {};
        response.data.forEach(agency => {
          map[agency.id] = agency.name;
        });
        setAgenciesMap(map);
      } catch (error) {
        console.error('Error fetching agencies:', error);
      }
    };
    
    fetchAgencies();
  }, [currentUser?.role, token]);

  // Cleanup camera stream when modal closes
  useEffect(() => {
    if (!showEditModal && cameraStream) {
      stopCamera();
    }
  }, [showEditModal]);

  // Fetch all users
  useEffect(() => {
    fetchUsers();
  }, [selectedAgency]); // Re-fetch when agency context changes

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Add organization_id filter if Super Admin has selected an agency
      let url = `${API}/users`;
      if (selectedAgency) {
        url += `?organization_id=${selectedAgency.id}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users. ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role: user.role,
      is_active: user.is_active,
      profile_image: user.profile_image || null
    });
    setShowEditModal(true);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' },
        audio: false 
      });
      setCameraStream(stream);
      setShowCameraModal(true);
      
      // Wait for modal to render, then set video stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error('Camera access error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Camera access denied. Please allow camera access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else {
        toast.error('Failed to access camera: ' + error.message);
      }
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCameraModal(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert to base64 with quality control to stay under 500KB
    let quality = 0.7;
    let imageData = canvas.toDataURL('image/jpeg', quality);
    
    // Check size and reduce quality if needed
    while (imageData.length > 500 * 1024 * 1.37 && quality > 0.1) {
      quality -= 0.1;
      imageData = canvas.toDataURL('image/jpeg', quality);
    }
    
    if (imageData.length > 500 * 1024 * 1.37) {
      toast.error('Photo is too large even after compression. Try taking photo in better lighting.');
      return;
    }
    
    // Set the captured image
    setEditingUser({...editingUser, profile_image: imageData});
    stopCamera();
    toast.success('Photo captured successfully!');
  };

  const handleSaveUser = async () => {
    try {
      const updateData = {
        first_name: editingUser.first_name,
        last_name: editingUser.last_name,
        role: editingUser.role,
        is_active: editingUser.is_active
      };
      
      // Only include email if Super Admin and email has changed
      if (currentUser.role === 'Super Admin') {
        updateData.email = editingUser.email;
      }
      
      // Include profile image if changed
      if (editingUser.profile_image !== undefined) {
        updateData.profile_image = editingUser.profile_image;
      }
      
      await axios.put(
        `${API}/users/${editingUser.id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      toast.success('User updated successfully!');
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user. ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await axios.put(
        `${API}/users/${userId}`,
        {
          is_active: !currentStatus
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Failed to update user status. ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteUser = async (user) => {
    // Prevent deleting yourself
    if (user.id === currentUser.id) {
      toast.error('You cannot delete your own account!');
      return;
    }

    // Confirmation dialog
    const confirmMessage = `Are you sure you want to permanently delete ${user.first_name} ${user.last_name} (${user.email})?\n\nThis action cannot be undone!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/users/${user.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      toast.success(`User ${user.first_name} ${user.last_name} deleted successfully!`);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user. ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddUser = () => {
    setNewUser({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'User',
      is_active: true
    });
    setShowAddModal(true);
  };

  const handleCreateUser = async () => {
    // Validate required fields
    if (!newUser.email || !newUser.password || !newUser.first_name || !newUser.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate password length
    if (newUser.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    try {
      const registerData = {
        email: newUser.email,
        password: newUser.password,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        role: newUser.role
      };
      
      // Add agency registration code if provided
      if (newUser.agency_registration_code && newUser.agency_registration_code.trim()) {
        registerData.agency_code = newUser.agency_registration_code.trim();
      }
      
      await axios.post(`${API}/auth/register`, registerData);
      toast.success(`User ${newUser.first_name} ${newUser.last_name} created successfully!`);
      setShowAddModal(false);
      // Reset form
      setNewUser({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'User',
        is_active: true,
        agency_registration_code: ''
      });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user. ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggle2FA = async (userId, userName, currentStatus) => {
    // Only Super Admin or Agency Admin can toggle 2FA (same-agency boundary enforced by backend + JSX gate)
    if (currentUser?.role !== 'Super Admin' && currentUser?.role !== 'Agency Admin') {
      toast.error('Only Admins can manage 2FA settings');
      return;
    }

    const action = currentStatus ? 'disable' : 'enable';
    const confirmMessage = currentStatus 
      ? `Are you sure you want to disable 2FA for ${userName}? This will make their account less secure.`
      : `Enable 2FA for ${userName}? They will need to set up their authenticator app on next login.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setToggling2FA(true);
    try {
      // Update user's totp_enabled status
      await axios.put(
        `${API}/users/${userId}`,
        { totp_enabled: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`2FA ${action}d for ${userName}`);
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error(`Error toggling 2FA:`, error);
      toast.error(`Failed to ${action} 2FA: ` + (error.response?.data?.detail || error.message));
    } finally {
      setToggling2FA(false);
    }
  };

  const handlePromoteToAgencyAdmin = async (userId, userName) => {
    // Only Super Admin and Agency Admin can promote users
    if (currentUser?.role !== 'Super Admin' && currentUser?.role !== 'Agency Admin') {
      toast.error('Only Admins can promote users');
      return;
    }

    if (!window.confirm(`Promote ${userName} to Agency Admin? They will be able to manage users and settings within the agency.`)) {
      return;
    }

    try {
      await axios.put(
        `${API}/users/${userId}`,
        { role: 'Agency Admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${userName} promoted to Agency Admin`);
      fetchUsers();
    } catch (error) {
      console.error('Error promoting user:', error);
      toast.error('Failed to promote user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDemoteFromAgencyAdmin = async (userId, userName) => {
    // Only Super Admin and Agency Admin can demote users
    if (currentUser?.role !== 'Super Admin' && currentUser?.role !== 'Agency Admin') {
      toast.error('Only Admins can demote users');
      return;
    }

    if (!window.confirm(`Demote ${userName} from Agency Admin to User?`)) {
      return;
    }

    try {
      await axios.put(
        `${API}/users/${userId}`,
        { role: 'User' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${userName} demoted to User`);
      fetchUsers();
    } catch (error) {
      console.error('Error demoting user:', error);
      toast.error('Failed to demote user: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    // Agency Admins cannot see Super Admin users
    if (currentUser?.role === 'Agency Admin' && user.role === 'Super Admin') {
      return false;
    }
    
    const matchesSearch = 
      user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    const matchesAgency = agencyFilter === 'all' || user.organization_id === agencyFilter;
    
    return matchesSearch && matchesRole && matchesAgency;
  });

  // User statistics - filter out Super Admins for Agency Admins
  const visibleUsers = currentUser?.role === 'Agency Admin' 
    ? users.filter(u => u.role !== 'Super Admin')
    : users;
    
  const stats = {
    total: visibleUsers.length,
    active: visibleUsers.filter(u => u.is_active).length,
    inactive: visibleUsers.filter(u => !u.is_active).length,
    loggedIn: visibleUsers.filter(u => u.is_logged_in).length,
    superAdmins: visibleUsers.filter(u => u.role === 'Super Admin').length,
    agencyAdmins: visibleUsers.filter(u => u.role === 'Agency Admin').length,
    regularUsers: visibleUsers.filter(u => u.role === 'User').length,
    viewers: visibleUsers.filter(u => u.role === 'Viewer').length
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Super Admin': return 'bg-red-600';
      case 'Agency Admin': return 'bg-orange-600';
      case 'User': return 'bg-blue-600';
      case 'Viewer': return 'bg-gray-600';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`${theme.colors.card} rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] flex flex-col relative z-[10000]`}>
        {/* Header */}
        <div className={`p-6 border-b ${theme.colors.border}`}>
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center">
                <UserCog className="h-6 w-6 mr-2 text-orange-500" />
                User Management
                {currentUser?.agency_name && (
                  <Badge className="ml-3 bg-blue-600 text-white">{currentUser.agency_name}</Badge>
                )}
              </h2>
              <div className="flex items-center gap-3 mt-1">
                <p className={`text-sm ${theme.colors.textMuted}`}>
                  Manage user accounts and permissions
                </p>
                {users.length > 0 && currentUser?.role === 'Agency Admin' && (
                  <Badge className="bg-green-600 text-white">
                    {users.length} / 150 Users
                  </Badge>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className={`${theme.colors.textMuted} hover:text-white transition-colors`}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="p-6 border-b border-gray-700">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-gray-800 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">Total Users</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
            <div className="bg-green-900 bg-opacity-30 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">Active</p>
              <p className="text-2xl font-bold text-green-400">{stats.active}</p>
            </div>
            <div className="bg-emerald-900 bg-opacity-30 p-4 rounded-lg">
              <p className="text-gray-400 text-sm flex items-center gap-1">
                <div className="h-2 w-2 bg-emerald-400 rounded-full animate-pulse"></div>
                Logged In
              </p>
              <p className="text-2xl font-bold text-emerald-400">{stats.loggedIn}</p>
            </div>
            <div className="bg-red-900 bg-opacity-30 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">Inactive</p>
              <p className="text-2xl font-bold text-red-400">{stats.inactive}</p>
            </div>
            <div className="bg-orange-900 bg-opacity-30 p-4 rounded-lg">
              <p className="text-gray-400 text-sm">Admins</p>
              <p className="text-2xl font-bold text-orange-400">{stats.superAdmins + stats.agencyAdmins}</p>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${theme.colors.card}`}
              />
            </div>
            <div className="w-full md:w-48">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                  <SelectValue>
                    {roleFilter === 'all' ? 'All Roles' : roleFilter}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600 z-[10001]">
                  <SelectItem value="all" className="text-white hover:bg-gray-700 focus:bg-gray-700">
                    All Roles
                  </SelectItem>
                  {currentUser?.role === 'Super Admin' && (
                    <SelectItem value="Super Admin" className="text-white hover:bg-gray-700 focus:bg-gray-700">
                      Super Admin
                    </SelectItem>
                  )}
                  <SelectItem value="Agency Admin" className="text-white hover:bg-gray-700 focus:bg-gray-700">
                    Agency Admin
                  </SelectItem>
                  <SelectItem value="User" className="text-white hover:bg-gray-700 focus:bg-gray-700">
                    User
                  </SelectItem>
                  <SelectItem value="Viewer" className="text-white hover:bg-gray-700 focus:bg-gray-700">
                    Viewer
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {currentUser?.role === 'Super Admin' && (
              <div className="w-full md:w-48">
                <Select value={agencyFilter} onValueChange={setAgencyFilter}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue>
                      {agencyFilter === 'all' ? 'All Agencies' : (agenciesMap[agencyFilter] || 'Select Agency')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600 z-[10001]">
                    <SelectItem value="all" className="text-white hover:bg-gray-700 focus:bg-gray-700">
                      All Agencies
                    </SelectItem>
                    {agencies.map((agency) => (
                      <SelectItem key={agency.id} value={agency.id} className="text-white hover:bg-gray-700 focus:bg-gray-700">
                        {agency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {currentUser.role === 'Super Admin' && (
              <Button
                onClick={handleAddUser}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            )}
            <Button
              onClick={handleSyncUsersToParticipants}
              disabled={syncingParticipants}
              variant="outline"
              className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              data-testid="sync-users-to-participants-btn"
              title="Create Participant cards for any users who don't already have one (matched by email)"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              {syncingParticipants ? 'Syncing…' : 'Full Sync (Users ↔ Participants)'}
            </Button>
          </div>
        </div>

        {/* User List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <p className={theme.colors.textMuted}>Loading users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <p className={theme.colors.textSecondary}>No users found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className={`${theme.colors.tertiary} border ${theme.colors.border} rounded-lg p-4 flex items-center justify-between hover:border-orange-500 transition-colors`}
                >
                  <div className="flex items-center space-x-4 flex-1">
                    {user.profile_image ? (
                      <img 
                        src={user.profile_image} 
                        alt={`${user.first_name} ${user.last_name}`}
                        className="h-12 w-12 rounded-full object-cover border-2 border-orange-500"
                      />
                    ) : (
                      <div className="bg-orange-600 text-white rounded-full h-12 w-12 flex items-center justify-center font-bold">
                        {user.first_name[0]}{user.last_name[0]}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <p className="font-semibold text-white">
                          {user.first_name} {user.last_name}
                        </p>
                        <Badge className={`${getRoleBadgeColor(user.role)} text-white`}>
                          {user.role}
                        </Badge>
                        {!user.is_active && (
                          <Badge className="bg-red-600 text-white">
                            Inactive
                          </Badge>
                        )}
                        {user.id === currentUser.id && (
                          <Badge className="bg-green-600 text-white">
                            You
                          </Badge>
                        )}
                        {user.is_logged_in ? (
                          <Badge className="bg-emerald-500 text-white flex items-center gap-1">
                            <div className="h-2 w-2 bg-white rounded-full animate-pulse"></div>
                            Logged In
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-600 text-gray-300 flex items-center gap-1">
                            <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                            Logged Out
                          </Badge>
                        )}
                        {user.totp_enabled && (
                          <Badge className="bg-blue-600 text-white flex items-center gap-1">
                            <Shield className="h-3 w-3" />
                            2FA
                          </Badge>
                        )}
                        {user.organization_id && (
                          <Badge className="bg-purple-600 text-white flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {agenciesMap[user.organization_id] || 'Unknown Agency'}
                          </Badge>
                        )}
                      </div>
                      <p className={`text-sm ${theme.colors.textMuted}`}>
                        {user.email}
                      </p>
                      <p className={`text-xs ${theme.colors.textMuted}`}>
                        Joined: {new Date(user.created_at).toLocaleDateString()}
                        {user.last_login && (
                          <span className="ml-2">
                            • Last login: {new Date(user.last_login).toLocaleString()}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => handleEditUser(user)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      size="sm"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    {(currentUser?.role === 'Super Admin' ||
                      (currentUser?.role === 'Agency Admin' &&
                        user.organization_id === currentUser?.organization_id &&
                        user.id !== currentUser.id)) && (
                      <Button
                        onClick={() => handleToggle2FA(user.id, `${user.first_name} ${user.last_name}`, user.totp_enabled)}
                        disabled={toggling2FA}
                        className={`${user.totp_enabled ? 'bg-orange-600 hover:bg-orange-700' : 'bg-purple-600 hover:bg-purple-700'} text-white`}
                        size="sm"
                        title={user.totp_enabled ? 'Disable 2FA' : 'Enable 2FA'}
                        data-testid={`toggle-2fa-btn-${user.id}`}
                      >
                        <Shield className="h-4 w-4 mr-1" />
                        {user.totp_enabled ? 'Disable 2FA' : 'Enable 2FA'}
                      </Button>
                    )}
                    {/* Promote/Demote Agency Admin */}
                    {(currentUser?.role === 'Super Admin' || currentUser?.role === 'Agency Admin') && user.id !== currentUser.id && (
                      <>
                        {user.role === 'User' || user.role === 'Viewer' ? (
                          <Button
                            onClick={() => handlePromoteToAgencyAdmin(user.id, `${user.first_name} ${user.last_name}`)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            size="sm"
                            title="Promote to Agency Admin"
                          >
                            <ArrowUp className="h-4 w-4 mr-1" />
                            Promote
                          </Button>
                        ) : user.role === 'Agency Admin' && currentUser?.role === 'Super Admin' ? (
                          <Button
                            onClick={() => handleDemoteFromAgencyAdmin(user.id, `${user.first_name} ${user.last_name}`)}
                            className="bg-gray-600 hover:bg-gray-700 text-white"
                            size="sm"
                            title="Demote to User"
                          >
                            <ArrowDown className="h-4 w-4 mr-1" />
                            Demote
                          </Button>
                        ) : null}
                      </>
                    )}
                    {user.id !== currentUser.id && (
                      <>
                        <Button
                          onClick={() => handleToggleStatus(user.id, user.is_active)}
                          className={`${user.is_active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
                          size="sm"
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        {currentUser.role === 'Super Admin' && (
                          <Button
                            onClick={() => handleDeleteUser(user)}
                            className="bg-red-700 hover:bg-red-800 text-white border border-red-500"
                            size="sm"
                            title="Permanently delete this user"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t ${theme.colors.border} flex justify-between items-center`}>
          <p className={`text-sm ${theme.colors.textMuted}`}>
            Showing {filteredUsers.length} of {users.length} users
          </p>
          <Button onClick={onClose} className="bg-orange-500 hover:bg-orange-600 text-black">
            Close
          </Button>
        </div>
      </div>

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[10001] p-4">
          <div className={`${theme.colors.card} rounded-lg shadow-xl max-w-md w-full relative z-[10002] max-h-[90vh] overflow-y-auto`}>
            <div className={`p-6 border-b ${theme.colors.border}`}>
              <h3 className="text-xl font-bold text-white">Edit User</h3>
            </div>
            <div className="p-6 space-y-4">
              {/* Profile Image Section */}
              <div>
                <Label>Profile Image</Label>
                <div
                  {...editUserImageDropProps}
                  data-testid="edit-user-image-dropzone"
                  className={`mt-2 space-y-3 rounded-lg border-2 border-dashed p-4 transition-colors ${
                    isEditUserImageDragging ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500' : 'border-gray-700'
                  }`}
                  title="Drop an image anywhere in this area to upload"
                >
                  <p className="text-xs text-gray-400 text-center">
                    {isEditUserImageDragging ? 'Drop to upload profile image' : 'Drag & drop an image here, or use the buttons below'}
                  </p>
                  {/* Image Preview */}
                  {editingUser.profile_image && (
                    <div className="flex justify-center">
                      <div className="relative">
                        <img 
                          src={editingUser.profile_image} 
                          alt="Profile" 
                          className="w-32 h-32 rounded-full object-cover border-2 border-orange-500"
                        />
                        <button
                          onClick={() => setEditingUser({...editingUser, profile_image: null})}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          title="Remove image"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Upload/Camera Buttons */}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => document.getElementById('edit-user-image-upload').click()}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Image
                    </Button>
                    <Button
                      type="button"
                      onClick={startCamera}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Use Camera
                    </Button>
                  </div>
                  
                  {/* Hidden file input for upload */}
                  <input
                    id="edit-user-image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Use image compression utility for profile images (400x400, max 150KB)
                        const result = await processImageFile(file, 'profile');
                        if (result.success) {
                          setEditingUser({...editingUser, profile_image: result.data});
                        } else {
                          toast.error(result.error || 'Failed to process image');
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  
                  <p className="text-xs text-gray-400">Images auto-compressed to max 150KB</p>
                </div>
              </div>
              
              <div>
                <Label>First Name</Label>
                <Input
                  value={editingUser.first_name}
                  onChange={(e) => setEditingUser({...editingUser, first_name: e.target.value})}
                  className={`${theme.colors.card}`}
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={editingUser.last_name}
                  onChange={(e) => setEditingUser({...editingUser, last_name: e.target.value})}
                  className={`${theme.colors.card}`}
                />
              </div>
              <div>
                <Label>Email {currentUser.role === 'Super Admin' ? '' : '(Read Only)'}</Label>
                <Input
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  disabled={currentUser.role !== 'Super Admin'}
                  className={`${theme.colors.card} ${currentUser.role !== 'Super Admin' ? 'opacity-50' : ''}`}
                  type="email"
                />
                {currentUser.role === 'Super Admin' && (
                  <p className="text-xs text-gray-400 mt-1">
                    Only Super Admin can edit email addresses
                  </p>
                )}
              </div>
              <div>
                <Label>Role</Label>
                <Select
                  value={editingUser.role}
                  onValueChange={(value) => setEditingUser({...editingUser, role: value})}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600 z-[10003]">
                    {currentUser?.role === 'Super Admin' && (
                      <SelectItem value="Super Admin" className="text-white hover:bg-gray-700 focus:bg-gray-700">
                        Super Admin
                      </SelectItem>
                    )}
                    <SelectItem value="Agency Admin" className="text-white hover:bg-gray-700 focus:bg-gray-700">
                      Agency Admin
                    </SelectItem>
                    <SelectItem value="User" className="text-white hover:bg-gray-700 focus:bg-gray-700">
                      User
                    </SelectItem>
                    <SelectItem value="Viewer" className="text-white hover:bg-gray-700 focus:bg-gray-700">
                      Viewer
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={editingUser.is_active}
                  onCheckedChange={(checked) => setEditingUser({...editingUser, is_active: checked})}
                />
                <Label>Active Account</Label>
              </div>
            </div>
            <div className={`p-4 border-t ${theme.colors.border} flex justify-end space-x-2`}>
              <Button
                onClick={() => setShowEditModal(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveUser}
                className="bg-orange-500 hover:bg-orange-600 text-black"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Camera Capture Modal */}
      {showCameraModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[10003] p-4">
          <div className="bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full relative z-[10004]">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white flex items-center">
                <Camera className="h-5 w-5 mr-2 text-green-500" />
                Capture Photo
              </h3>
              <button
                onClick={stopCamera}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Video Preview */}
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-auto"
                  style={{ maxHeight: '60vh' }}
                />
                {/* Circular guide overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-64 h-64 rounded-full border-4 border-orange-500 border-dashed opacity-50"></div>
                </div>
              </div>
              
              {/* Hidden canvas for capture */}
              <canvas ref={canvasRef} className="hidden" />
              
              {/* Instructions */}
              <p className="text-center text-gray-300 text-sm">
                Position your face in the circle and click "Capture Photo"
              </p>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={stopCamera}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={capturePhoto}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Capture Photo
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[10001] p-4">
          <div className={`${theme.colors.card} rounded-lg shadow-xl max-w-md w-full relative z-[10002]`}>
            <div className={`p-6 border-b ${theme.colors.border}`}>
              <h3 className="text-xl font-bold text-white flex items-center">
                <UserPlus className="h-5 w-5 mr-2 text-green-500" />
                Add New User
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>Agency Registration Code</Label>
                <Input
                  value={newUser.agency_registration_code}
                  onChange={(e) => setNewUser({...newUser, agency_registration_code: e.target.value})}
                  className={`${theme.colors.card}`}
                  placeholder="Enter agency code (optional)"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Optional: Enter agency code to automatically assign user to an agency
                </p>
              </div>
              <div>
                <Label>First Name *</Label>
                <Input
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                  className={`${theme.colors.card}`}
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                  className={`${theme.colors.card}`}
                  placeholder="Enter last name"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className={`${theme.colors.card}`}
                  type="email"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <Label>Password *</Label>
                <Input
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className={`${theme.colors.card}`}
                  type="password"
                  placeholder="Minimum 8 characters"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Password must be at least 8 characters long
                </p>
              </div>
              <div>
                <Label>Role</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({...newUser, role: value})}
                >
                  <SelectTrigger className={`${theme.colors.card}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={`${theme.colors.card}`}>
                    {currentUser?.role === 'Super Admin' && (
                      <SelectItem value="Super Admin">Super Admin</SelectItem>
                    )}
                    <SelectItem value="Agency Admin">Agency Admin</SelectItem>
                    <SelectItem value="User">User</SelectItem>
                    <SelectItem value="Viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={newUser.is_active}
                  onCheckedChange={(checked) => setNewUser({...newUser, is_active: checked})}
                />
                <Label>Active Account</Label>
              </div>
            </div>
            <div className={`p-4 border-t ${theme.colors.border} flex justify-end space-x-2`}>
              <Button
                onClick={() => setShowAddModal(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateUser}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Create User
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default UserManagement;
